import { anatomy } from "@chakra-ui/anatomy";
import {
  type StyleFunctionProps,
  createMultiStyleConfigHelpers,
} from "@chakra-ui/react";

const parts = anatomy("dataSummaryCard").parts(
  "container",
  "card",
  "content",
  "title",
  "mainText",
  "secondaryText",
);

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(parts.keys);

const baseStyle = definePartsStyle({
  container: {
    display: "flex",
    position: "relative",
    borderRadius: "lg",
    minWidth: "150px",
    w: "100%",
    border: "1px solid",
    overflow: "hidden",
    alignItems: "stretch",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    w: "100%",
    p: 4,
    gap: 3,
    borderRadius: "lg",
  },
  title: {
    fontSize: "sm",
    textTransform: "uppercase",
    color: "gray.900",
  },
  tooltip: {
    pos: "absolute",
    top: "3",
    right: "3",
  },
  tooltipButton: {
    cursor: "help !important",
  },
  mainText: {
    fontSize: "md",
    fontWeight: "bold",
    color: "gray.900",
    lineHeight: "1.3",
  },
  secondaryText: {
    mt: -3,
    fontSize: "md",
    color: "gray.900",
  },
});

const variants = {
  unknown: definePartsStyle((props: StyleFunctionProps) => ({
    container: {
      borderColor: "gray.500",
    },
    card: {
      bg: "gray.100",
      ...(props.isClickable && {
        _hover: {
          bg: "gray.50",
          cursor: "pointer",
        },
      }),
    },
  })),
  secondary: definePartsStyle((props: StyleFunctionProps) => ({
    container: {
      borderColor: "secondary.500",
    },
    card: {
      bg: "secondary.100",
      ...(props.isClickable && {
        _hover: {
          bg: "secondary.50",
          cursor: "pointer",
        },
      }),
    },
  })),
  success: definePartsStyle((props: StyleFunctionProps) => ({
    container: {
      borderColor: "green.500",
    },
    card: {
      bg: "green.100",
      ...(props.isClickable && {
        _hover: {
          bg: "green.50",
          cursor: "pointer",
        },
      }),
    },
  })),
  review: definePartsStyle((props: StyleFunctionProps) => ({
    container: {
      borderColor: "yellow.500",
    },
    card: {
      bg: "yellow.100",
      ...(props.isClickable && {
        _hover: {
          bg: "yellow.50",
          cursor: "pointer",
        },
      }),
    },
  })),
  info: definePartsStyle((props: StyleFunctionProps) => ({
    container: {
      borderColor: "blue.500",
    },
    card: {
      bg: "blue.100",
      ...(props.isClickable && {
        _hover: {
          bg: "blue.50",
          cursor: "pointer",
        },
      }),
    },
  })),
  error: definePartsStyle((props: StyleFunctionProps) => ({
    container: {
      borderColor: "red.500",
    },
    card: {
      bg: "red.100",
      ...(props.isClickable && {
        _hover: {
          bg: "red.50",
          cursor: "pointer",
        },
      }),
    },
  })),
};

const sizes = {
  sm: definePartsStyle({
    card: {
      gap: 2,
    },
    title: {
      fontSize: "xs",
      fontWeight: "normal",
    },
    mainText: {
      fontSize: "md",
      fontWeight: "semibold",
    },
  }),
  md: definePartsStyle({
    title: {
      fontSize: "sm",
      fontWeight: "semibold",
    },
    mainText: {
      fontSize: "lg",
    },
  }),
  lg: definePartsStyle({
    title: {
      fontSize: "md",
      fontWeight: "semibold",
    },
    mainText: {
      fontSize: "xl",
    },
  }),
};

export const dataSummaryCardTheme = defineMultiStyleConfig({
  baseStyle,
  variants,
  sizes,
});
